
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ModuleTypeEnum,
  PreferenceContextEnum,
  PreferenceScopeEnum,
  ViewConfig,
  ViewPreferences,
  SavedView,
  UserPreferenceData,
} from '@scrambl/shared';
import { userPreferencesApi } from '@/lib/api/user-preferences.api';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const logger = createLogger('hooks/useModuleViews');

interface UseModuleViewsOptions {
  moduleType: ModuleTypeEnum;
  canManage?: boolean;
}

interface UseModuleViewsResult {
  views: SavedView[];
  userViews: SavedView[];
  orgViews: SavedView[];
  activeViewId?: string;
  activeView?: SavedView;
  orgDefaultViewId?: string;
  isLoading: boolean;
  setActiveViewId: (viewId: string) => Promise<void>;
  saveActiveView: (config: ViewConfig) => Promise<void>;
  createView: (name: string, config: ViewConfig, scope?: PreferenceScopeEnum) => Promise<void>;
  setOrgDefaultViewId: (viewId: string) => Promise<void>;
  deleteView: (viewId: string) => Promise<void>;
}

const EMPTY_PREFS: ViewPreferences = { views: [] };

const createViewId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `view_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const normalizeViews = (views: SavedView[] | undefined, scope: PreferenceScopeEnum): SavedView[] => {
  return (views || []).map(view => ({
    id: view.id || createViewId(),
    name: view.name || 'Untitled view',
    scope: view.scope || scope,
    config: view.config || {},
    createdAt: view.createdAt,
    updatedAt: view.updatedAt,
  }));
};

const toViewPreferences = (preferences?: Record<string, unknown>): ViewPreferences => {
  if (!preferences) return EMPTY_PREFS;
  const rawViews = (preferences as { views?: unknown }).views;
  const activeViewId = (preferences as { activeViewId?: unknown }).activeViewId;
  const defaultViewId = (preferences as { defaultViewId?: unknown }).defaultViewId;

  return {
    views: Array.isArray(rawViews) ? (rawViews as SavedView[]) : [],
    activeViewId: typeof activeViewId === 'string' ? activeViewId : undefined,
    defaultViewId: typeof defaultViewId === 'string' ? defaultViewId : undefined,
  };
};

const toPreferencesRecord = (preferences: ViewPreferences): Record<string, unknown> =>
  preferences as unknown as Record<string, unknown>;

export function useModuleViews({ moduleType, canManage = false }: UseModuleViewsOptions): UseModuleViewsResult {
  const queryClient = useQueryClient();

  const {
    data: userPreferenceData,
    isLoading: isLoadingUser,
  } = useQuery({
    queryKey: ['view-preferences', moduleType, 'user'],
    queryFn: () =>
      userPreferencesApi.getUserPreferences(
        PreferenceContextEnum.VIEWS,
        moduleType,
        PreferenceScopeEnum.USER,
      ) as Promise<UserPreferenceData | null>,
  });

  const {
    data: orgPreferenceData,
    isLoading: isLoadingOrg,
  } = useQuery({
    queryKey: ['view-preferences', moduleType, 'org'],
    queryFn: () =>
      userPreferencesApi.getUserPreferences(
        PreferenceContextEnum.VIEWS,
        moduleType,
        PreferenceScopeEnum.ORGANIZATION,
      ) as Promise<UserPreferenceData | null>,
  });

  const userPreferences = toViewPreferences(userPreferenceData?.preferences);
  const orgPreferences = toViewPreferences(orgPreferenceData?.preferences);

  const userViews = normalizeViews(userPreferences.views, PreferenceScopeEnum.USER);
  const orgViews = normalizeViews(orgPreferences.views, PreferenceScopeEnum.ORGANIZATION);

  const views = [...orgViews, ...userViews];

  const orgDefaultViewId = orgPreferences.defaultViewId;
  const resolvedActiveViewId =
    userPreferences.activeViewId || orgDefaultViewId || views[0]?.id;
  const activeView = views.find(view => view.id === resolvedActiveViewId) || views[0];

  const refreshQueries = async () => {
    await queryClient.invalidateQueries({ queryKey: ['view-preferences', moduleType] });
  };

  const saveUserPreferences = async (nextPreferences: ViewPreferences) => {
    await userPreferencesApi.saveUserPreferences(
      PreferenceContextEnum.VIEWS,
      toPreferencesRecord(nextPreferences),
      moduleType,
      PreferenceScopeEnum.USER,
    );
    await refreshQueries();
  };

  const saveOrgPreferences = async (nextPreferences: ViewPreferences) => {
    await userPreferencesApi.saveUserPreferences(
      PreferenceContextEnum.VIEWS,
      toPreferencesRecord(nextPreferences),
      moduleType,
      PreferenceScopeEnum.ORGANIZATION,
    );
    await refreshQueries();
  };

  const setActiveViewId = async (viewId: string) => {
    const nextPreferences: ViewPreferences = {
      ...userPreferences,
      views: userViews,
      activeViewId: viewId,
    };
    try {
      await saveUserPreferences(nextPreferences);
    } catch (error) {
      logger.error('[useModuleViews] Failed to set active view', error);
      toast.error('Failed to update view selection');
    }
  };

  const createView = async (
    name: string,
    config: ViewConfig,
    scope: PreferenceScopeEnum = PreferenceScopeEnum.USER
  ) => {
    const targetScope = scope === PreferenceScopeEnum.ORGANIZATION && canManage
      ? PreferenceScopeEnum.ORGANIZATION
      : PreferenceScopeEnum.USER;
    const now = new Date().toISOString();
    const newView: SavedView = {
      id: createViewId(),
      name: name.trim() || 'New view',
      scope: targetScope,
      config,
      createdAt: now,
      updatedAt: now,
    };

    try {
      if (targetScope === PreferenceScopeEnum.ORGANIZATION) {
        await saveOrgPreferences({
          ...orgPreferences,
          views: [...orgViews, newView],
        });
      } else {
        await saveUserPreferences({
          ...userPreferences,
          views: [...userViews, newView],
          activeViewId: newView.id,
        });
      }
    } catch (error) {
      logger.error('[useModuleViews] Failed to create view', error);
      toast.error('Failed to create view');
    }
  };

  const saveActiveView = async (config: ViewConfig) => {
    const now = new Date().toISOString();
    if (!activeView) {
      await createView('My view', config, PreferenceScopeEnum.USER);
      return;
    }

    const updatedView: SavedView = {
      ...activeView,
      config,
      updatedAt: now,
    };

    try {
      if (activeView.scope === PreferenceScopeEnum.ORGANIZATION) {
        if (!canManage) {
          await createView(`My ${activeView.name}`, config, PreferenceScopeEnum.USER);
          return;
        }
        const nextViews = orgViews.map(view =>
          view.id === activeView.id ? updatedView : view,
        );
        await saveOrgPreferences({
          ...orgPreferences,
          views: nextViews,
        });
      } else {
        const nextViews = userViews.map(view =>
          view.id === activeView.id ? updatedView : view,
        );
        await saveUserPreferences({
          ...userPreferences,
          views: nextViews,
          activeViewId: activeView.id,
        });
      }
    } catch (error) {
      logger.error('[useModuleViews] Failed to save view', error);
      toast.error('Failed to save view');
    }
  };

  const setOrgDefaultViewId = async (viewId: string) => {
    if (!canManage) return;
    if (!orgViews.some(view => view.id === viewId)) return;
    try {
      await saveOrgPreferences({
        ...orgPreferences,
        views: orgViews,
        defaultViewId: viewId,
      });
    } catch (error) {
      logger.error('[useModuleViews] Failed to set org default view', error);
      toast.error('Failed to update default view');
    }
  };

  const deleteView = async (viewId: string) => {
    const view = views.find(item => item.id === viewId);
    if (!view) return;

    const storedActiveViewId = userPreferences.activeViewId;

    if (view.scope === PreferenceScopeEnum.ORGANIZATION) {
      if (!canManage) {
        toast.error('You do not have permission to delete shared views');
        return;
      }

      const nextOrgViews = orgViews.filter(item => item.id !== viewId);
      const nextDefaultViewId =
        orgPreferences.defaultViewId === viewId
          ? nextOrgViews[0]?.id
          : orgPreferences.defaultViewId;

      try {
        await saveOrgPreferences({
          ...orgPreferences,
          views: nextOrgViews,
          defaultViewId: nextDefaultViewId,
        });

        if (storedActiveViewId === viewId) {
          const nextActiveViewId =
            nextDefaultViewId || nextOrgViews[0]?.id || userViews[0]?.id;
          await saveUserPreferences({
            ...userPreferences,
            views: userViews,
            activeViewId: nextActiveViewId,
          });
        }
      } catch (error) {
        logger.error('[useModuleViews] Failed to delete shared view', error);
        toast.error('Failed to delete shared view');
      }

      return;
    }

    const nextUserViews = userViews.filter(item => item.id !== viewId);
    const nextActiveViewId =
      storedActiveViewId === viewId
        ? orgPreferences.defaultViewId || orgViews[0]?.id || nextUserViews[0]?.id
        : storedActiveViewId;

    try {
      await saveUserPreferences({
        ...userPreferences,
        views: nextUserViews,
        activeViewId: nextActiveViewId,
      });
    } catch (error) {
      logger.error('[useModuleViews] Failed to delete personal view', error);
      toast.error('Failed to delete view');
    }
  };

  return {
    views,
    userViews,
    orgViews,
    activeViewId: resolvedActiveViewId,
    activeView,
    orgDefaultViewId,
    isLoading: isLoadingUser || isLoadingOrg,
    setActiveViewId,
    saveActiveView,
    createView,
    setOrgDefaultViewId,
    deleteView,
  };
}
